/**
 * Self-service off-boarding (17 Jul) — the CUSTOMER closes their own account.
 *
 * Sibling of DeleteTenantDialog (which is the SUPERADMIN removing a customer).
 * Same engine underneath: this POSTs /billing/cancel, which stamps the same
 * 30-day grace clock the superadmin flow uses, and the same overnight purge
 * job finishes the job. The differences are all deliberate:
 *   - it shows THEIR OWN numbers (from /billing/cancel-preview), and leads with
 *     the one that lands: WHEN their N employees lose access — the end of the
 *     period they've paid for (`close_on`, Terms §8, 22/09/2026), or
 *     immediately if nothing was paid (a trial; `close_on` null);
 *   - it offers the data download up front (admins keep their login through the
 *     grace, so this is reachable later too, but it belongs here);
 *   - it is warm, not scary. Nothing is deleted until the date, and one click
 *     brings it all back.
 */
import React, { useEffect, useState } from 'react';
import { AlertTriangle, Download, Loader2, DoorOpen } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { billingApi } from '../lib/api';
import { ACCOUNT_STATUS_CHANGED } from './AccountClosingBanner';
import { toast } from 'sonner';

// ISO (yyyy-mm-dd) -> DD/MM/YYYY. House convention: users never see ISO.
function gb(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

export default function CloseAccountDialog({ open, onOpenChange, onClosed }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmName, setConfirmName] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setConfirmName(''); setReason(''); setNotes('');
    billingApi.cancelPreview()
      .then((r) => setPreview(r.data))
      .catch(() => toast.error('Could not load your account details'))
      .finally(() => setLoading(false));
  }, [open]);

  const company = preview?.company_name || '';
  const nameMatches =
    confirmName.trim().toLowerCase() === company.trim().toLowerCase();

  const download = async () => {
    try {
      const r = await billingApi.exportOwnData();
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(company || 'company').replace(/\s+/g, '-')}-full-backup.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Could not export your data');
    }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const r = await billingApi.cancelAccount({
        confirm_name: confirmName,
        reason: reason || null,
        reason_notes: notes || null,
        marketing_opt_out: false,
      });
      toast.success(r.data?.message || 'Account scheduled to close');
      onOpenChange(false);
      window.dispatchEvent(new Event(ACCOUNT_STATUS_CHANGED));   // banner appears at once
      if (onClosed) onClosed();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not close the account');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-red-500">
            Close {company ? company : 'your'} account
          </DialogTitle>
          <DialogDescription>
            We're sorry to see you go. Please read what happens before you confirm.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : preview ? (
          <div className="space-y-4 mt-1">
            {/* The warning that actually works: people, not row counts. Two
                shapes: paid up (employees keep access to the period end) or
                nothing paid (they lose it now). Same numbers, honest date. */}
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
              {preview.close_on ? (
                <div>
                  <p className="font-medium">
                    Your account stays open until {gb(preview.close_on)} — the end of
                    the period you've paid for.
                  </p>
                  <p className="text-muted-foreground text-xs mt-1">
                    On that date your {preview.employees_lose_access} employee(s) lose
                    access and they'll be told to contact you. Your admins can still
                    sign in and download data for {preview.grace_days} more days.
                    {preview.pending_requests > 0 && (
                      <> {preview.pending_requests} leave request(s) awaiting approval
                        will need actioning before then.</>
                    )}
                  </p>
                </div>
              ) : (
                <div>
                  <p className="font-medium">
                    Your {preview.employees_lose_access} employee(s) will lose access
                    immediately.
                  </p>
                  <p className="text-muted-foreground text-xs mt-1">
                    They'll be told to contact you. Your admins can still sign in and
                    download data during the grace period.
                    {preview.pending_requests > 0 && (
                      <> {preview.pending_requests} leave request(s) awaiting approval
                        will never be actioned.</>
                    )}
                  </p>
                </div>
              )}
            </div>

            {/* What's on the line, in their own numbers. */}
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-muted/50 p-2">
                <div className="text-lg font-semibold">{preview.employees}</div>
                <div className="text-[11px] text-muted-foreground uppercase tracking-wide">Employees</div>
              </div>
              <div className="rounded-lg bg-muted/50 p-2">
                <div className="text-lg font-semibold">{preview.leave_requests}</div>
                <div className="text-[11px] text-muted-foreground uppercase tracking-wide">Leave records</div>
              </div>
              <div className="rounded-lg bg-muted/50 p-2">
                <div className="text-lg font-semibold">{preview.years_of_history}</div>
                <div className="text-[11px] text-muted-foreground uppercase tracking-wide">Years of history</div>
              </div>
            </div>

            <p className="text-sm text-muted-foreground">
              Nothing is deleted until <strong>{gb(preview.purge_on)}</strong>. Until
              then you can {preview.close_on ? 'undo this' : 'bring the account back'} with
              one click. After that date it is permanently deleted and cannot be recovered.
            </p>

            <Button type="button" variant="outline" onClick={download} className="w-full"
                    data-testid="close-account-export">
              <Download className="w-4 h-4 mr-2" />
              Download all your data first
            </Button>

            <div>
              <Label>Why are you leaving? <span className="text-muted-foreground">(optional)</span></Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger className="mt-1" data-testid="close-account-reason">
                  <SelectValue placeholder="Choose a reason" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(preview.reasons || {}).map(([k, label]) => (
                    <SelectItem key={k} value={k}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {reason && (
                <Input value={notes} onChange={(e) => setNotes(e.target.value)}
                       className="input-hive mt-2" placeholder="Anything you'd like to add? (optional)"
                       data-testid="close-account-notes" />
              )}
            </div>

            <div>
              <Label>Type the company name to confirm: <span className="font-semibold">{company}</span></Label>
              <Input value={confirmName} onChange={(e) => setConfirmName(e.target.value)}
                     className="input-hive mt-1" placeholder={company}
                     data-testid="close-account-confirm" />
            </div>

            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
                Keep our account
              </Button>
              <Button type="button" className="bg-red-600 hover:bg-red-700 text-white"
                      onClick={confirm} disabled={busy || !nameMatches}
                      data-testid="close-account-go">
                {busy ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Closing…</>)
                  : (<><DoorOpen className="w-4 h-4 mr-2" />Close account</>)}
              </Button>
            </div>
          </div>
        ) : (
          <p className="py-6 text-sm text-muted-foreground text-center">
            Couldn't load your account details. Please close this and try again.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
